import { Controller } from "@hotwired/stimulus"
import SlimSelect from "slim-select"
import { slimSelectSettings } from "../helpers/slim_select_helpers"

// Minimum query length before the server is queried in async-search mode.
const MIN_SERVER_SEARCH_LENGTH = 2
// Collapse a burst of keystrokes into a single request — the search endpoint embeds the
// query via a rate-limited external API, so one request per keystroke must be avoided.
const SERVER_SEARCH_DEBOUNCE_MS = 250

// Whether an option's text contains every space-separated word of the search term.
const optionMatches = (text, search) => {
  const optionText = text.toLowerCase()
  return search
    .toLowerCase()
    .split(" ")
    .every((term) => optionText.includes(term))
}

// SlimSelect data narrowed to a (short) term; an empty term keeps it whole. Options another
// controller hid (display: false) stay out. An optgroup whose own label matches keeps all its
// options — searching a Baustelle offers its Aufgaben.
const catalogMatching = (catalog, term) => {
  if (term.length === 0) return catalog

  const visible = (option) => option.display !== false
  return catalog.flatMap((entry) => {
    if (!entry.options) return visible(entry) && optionMatches(entry.text, term) ? [entry] : []
    const shown = entry.options.filter(visible)
    const options = optionMatches(entry.label, term)
      ? shown
      : shown.filter((option) => optionMatches(option.text, term))
    return options.length ? [{ ...entry, options }] : []
  })
}

export default class extends Controller {
  connect() {
    const searchUrl = this.element.dataset.searchUrl
    // Async mode: picking a search hit the list did not hold rewrites the native <select> down
    // to that one option, so the server-rendered list is kept here for resetOptions().
    if (searchUrl) this.renderedOptionsHtml = this.element.innerHTML
    // When the select lives inside a native <dialog> opened with showModal() — or inside a
    // popover shown with showPopover(), such as the Baustellen table's cell editors — that
    // ancestor paints in the top layer. SlimSelect appends its dropdown to document.body by
    // default, which would render behind it. Anchoring the dropdown to that ancestor (with
    // fixed positioning) keeps it in the top layer too.
    const topLayerHost = this.element.closest("dialog, [popover]")

    this.slimselect = new SlimSelect({
      select: this.element,
      settings: slimSelectSettings({
        searchPlaceholder: "Suchen",
        searchingText: this.element.dataset.searchingText || "Suchen …",
        placeholderText: this.element.dataset.placeholder
          ? this.element.dataset.placeholder
          : "Auswählen",
        // Clearing a required fixed enum posts an empty value and fails an inclusion
        // validation the user cannot see the cause of — data-allow-deselect="false"
        // makes such a select un-emptyable. Default stays true for optional filters.
        allowDeselect: this.element.dataset.allowDeselect !== "false",
        closeOnSelect: !this.element.getAttribute("multiple"),
        // A `multiple` select in a tight spot (a table header filter) shows its picks as one
        // truncated line ("A, B, C") instead of chips that wrap and grow the control.
        multiString: this.element.dataset.multiString === "true",
        showSearch: this.element.dataset.showSearch !== "false",
        ...(topLayerHost ? { contentLocation: topLayerHost, contentPosition: "fixed" } : {}),
        // Opt-in "„{value}“ neu anlegen" row for unknown search terms. The term is
        // NOT added as an option here — the addable event below hands it to whoever
        // listens for select:addable (e.g. a quick-create dialog) and cancels
        // SlimSelect's own client-side add by returning false. With
        // data-addable-value-prefix the term is added and picked instead.
        ...(this.element.dataset.addableText
          ? { addableText: this.element.dataset.addableText }
          : {}),
      }),
      // With data-search-url the dropdown queries the server (hybrid keyword +
      // semantic search) so synonyms surface; without it, the original client-side
      // substring filter over the pre-rendered options is kept.
      events: {
        afterClose: () => {
          if (!this.submitPending) return
          this.submitPending = false
          this.submit()
        },
        ...(searchUrl
          ? {
              search: (search, _selected, catalog) => this.serverSearch(searchUrl, search, catalog),
            }
          : {
              searchFilter: (option, search) => {
                // Options another controller filtered out (display: false) stay out of
                // the results — SlimSelect's search ignores `display`, so a term
                // matching only those renders a list of hide-classed rows: visually
                // empty, yet non-empty as far as the "neu anlegen" hint is concerned
                // (that row renders only on zero results).
                if (option.display === false) return false

                // If no search term, show all options
                if (search.length === 0) return true

                return optionMatches(option.text, search)
              },
            }),
        ...(this.element.dataset.addableText
          ? {
              addable: (term) => {
                // A picker that takes a typed-in name keeps it as its own option, its value
                // marked by data-addable-value-prefix (a report picker's `text:<name>`).
                const prefix = this.element.dataset.addableValuePrefix
                if (prefix) return { text: term, value: `${prefix}${term}` }

                this.slimselect.close()
                this.dispatch("addable", { detail: { term } })
                return false
              },
            }
          : {}),
      },
    })
  }

  // Resolve dropdown options from the server. Short queries — a cleared box included, which
  // slim-select ≥ 4.4 hands this callback too — filter SlimSelect's `catalog` locally instead:
  // the list as last loaded, with refresh()-hidden options, added options and the current
  // pick kept. Debounced and with the previous in-flight request aborted, so a burst of
  // keystrokes makes one query, not one per character. A superseded search is left pending
  // rather than settled: SlimSelect renders whatever a search promise resolves to, so settling
  // it would flash a stale list before the newer hits arrive. Rows are { id, label, data? }, or
  // { label, options: [{ id, label }] } for an <optgroup>. Returns a Promise of SlimSelect
  // option objects; rejects with a message SlimSelect shows inline.
  serverSearch(url, search, catalog) {
    const term = search.trim()
    this.cancelServerSearch()
    if (term.length < MIN_SERVER_SEARCH_LENGTH) return catalogMatching(catalog, term)

    return new Promise((resolve, reject) => {
      const abort = new AbortController()
      this.searchAbort = abort
      this.searchTimer = setTimeout(() => {
        // The search URL may carry its own query (e.g. `?catalog=1`); append `q` to it.
        const searchUrl = new URL(url, window.location.origin)
        searchUrl.searchParams.set("q", term)
        fetch(searchUrl, {
          headers: { Accept: "application/json" },
          signal: abort.signal,
        })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            return response.json()
          })
          .then((rows) => resolve(rows.map((row) => this.serverRowToData(row))))
          .catch((error) => {
            if (error.name === "AbortError") return
            reject(new Error(this.element.dataset.searchError || "Suche fehlgeschlagen"))
          })
      }, SERVER_SEARCH_DEBOUNCE_MS)
    })
  }

  serverRowToData(row) {
    if (Array.isArray(row.options)) {
      return {
        label: row.label,
        options: row.options.map((option) => this.serverRowToData(option)),
      }
    }
    // An optional `data` object rides along onto the native <option> as data-* attributes
    // (SlimSelect kebab-cases camelCase keys), e.g. the Artikel prefill payload.
    return { text: row.label, value: String(row.id), ...(row.data ? { data: row.data } : {}) }
  }

  // Put the server-rendered options back after a search replaced them (async mode only), e.g.
  // when a dialog re-opens the select for a fresh pick. Clears the current value.
  resetOptions() {
    if (this.renderedOptionsHtml === undefined) return

    this.cancelServerSearch()
    this.element.innerHTML = this.renderedOptionsHtml
    this.refresh()
  }

  // Cancel a pending debounce timer + in-flight request.
  cancelServerSearch() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
    }
    if (this.searchAbort) {
      this.searchAbort.abort()
      this.searchAbort = null
    }
  }

  // Rebuild SlimSelect's data from the current <select> DOM, preserving <optgroup>s.
  // Call this after another controller has mutated the underlying options: SlimSelect's own
  // MutationObserver only catches up asynchronously, this applies the change right away.
  //
  // Every option must be carried over, hidden ones included: setData recreates the
  // native options from this data, so an option left out is DELETED from the select,
  // not hidden. Hidden ones travel as `display: false` instead — SlimSelect writes
  // that back as the `hidden` attribute and hide-classes the dropdown row, so they
  // stay invisible but remain available to the next filter pass.
  refresh() {
    this.slimselect.setData(this.optionsDataFrom(this.element))
  }

  // The given <select>'s options as SlimSelect data, <optgroup>s preserved.
  optionsDataFrom(select) {
    // Mirrors SlimSelect's own Select#getDataFromOption — every attribute it would
    // read must be carried, because setData recreates the DOM options from this data
    // and resets anything missing. In particular `disabled` (a filtering controller's
    // no-JS guard) and `defaultSelected` (what a native form.reset() restores) are
    // silently cleared if omitted, and `data` keeps data-customer-id alive on site
    // options.
    //
    // A *selected* option must never be display-hidden by its filtering controller:
    // SlimSelect renders the value box from `selected && display`, so it would go
    // blank — for a `multiple` select the chip disappears while the value is still
    // submitted. Both current callers already guarantee that.
    const build = (o) => ({
      text: o.textContent.trim(),
      value: o.value,
      selected: o.selected,
      defaultSelected: o.defaultSelected,
      disabled: o.disabled,
      display: !o.hidden && o.style.display !== "none",
      data: { ...o.dataset },
    })
    // allowDeselect makes SlimSelect append its own synthetic placeholder option
    // (empty text, empty value) to the DOM select; copying it into the data would
    // show it as a blank selectable row and render an empty value box. This is the
    // only option that may be dropped rather than carried over hidden. A Rails
    // `include_blank: 'Kunde wählen'` prompt has text, so it is kept as a real option.
    const synthetic = (o) =>
      o.dataset.placeholder === "true" || (o.value === "" && o.textContent.trim() === "")
    const data = []
    for (const child of select.children) {
      if (child.tagName === "OPTGROUP") {
        const opts = [...child.children].filter((o) => !synthetic(o)).map(build)
        if (opts.length) data.push({ label: child.label, options: opts })
      } else if (child.tagName === "OPTION" && !synthetic(child)) {
        data.push(build(child))
      }
    }
    return data
  }

  disconnect() {
    this.cancelServerSearch()
    this.slimselect.destroy()
  }

  // `form` honours a select's `form` attribute (a filter in a table header submits a form
  // outside the table); for a select inside its form it is the same as closest("form").
  submit() {
    ;(this.element.form || this.element.closest("form")).requestSubmit()
  }

  // For a `multiple` filter: submit once the dropdown closes, not on every pick — the submit
  // reloads the page part and would shut the dropdown after the first value. A chip removed
  // (or the × clicked) with the dropdown closed submits right away.
  submitWhenClosed() {
    if (this.slimselect.settings.isOpen) this.submitPending = true
    else this.submit()
  }

  // Programmatic selection that actually sticks: SlimSelect can revert a bare
  // `select.value = x` + change on its first value change after init, so drive
  // its store first, then sync the native select and dispatch change so Stimulus
  // actions bound to the select still fire.
  setSelected(value) {
    this.slimselect.setSelected(value)
    this.element.value = value
    this.element.dispatchEvent(new Event("change"))
  }

  // Reset the selection to the blank option (used by dialogs that reuse a persistent select).
  // A multiple select clears to an empty array rather than the blank-option value.
  //
  // A single SlimSelect always has SOME option selected — with none flagged, setData picks the
  // first — so "blank" is its own placeholder option, the one its × button selects. That
  // placeholder is not guaranteed to exist here: refresh() drops it, so after a refresh()
  // setSelected("") matched nothing and the first real option won. Put it back explicitly.
  clear() {
    if (this.element.multiple) {
      this.slimselect.setSelected([])
      return
    }

    const unselect = (entry) =>
      entry.options
        ? { ...entry, options: entry.options.map(unselect) }
        : { ...entry, selected: false }
    const data = this.optionsDataFrom(this.element).map(unselect)
    this.slimselect.setData([{ text: "", value: "", placeholder: true, selected: true }, ...data])
  }
}
